/**
 * Local OCR Service API
 * Handles communication with the local OCR service for document text extraction
 * Compatible with Node.js 16.20.2
 */

// Use backend proxy instead of direct service to avoid CORS issues in production
const OCR_SERVICE_URL = '/api/ocr';
const OCR_API_ENDPOINT = `${OCR_SERVICE_URL}/process`;
const OCR_HEALTH_CHECK = `${OCR_SERVICE_URL}/health`;

/**
 * Check if OCR service is healthy
 * @returns {Promise<boolean>} True if service is healthy
 */
export const checkOCRServiceHealth = async () => {
  try {
    const response = await fetch(OCR_HEALTH_CHECK);
    if (response.ok) {
      const data = await response.json();
      console.log('✓ OCR Service is healthy:', data);
      return true;
    }
    console.warn('OCR Service health check failed:', response.status);
    return false;
  } catch (error) {
    console.error('OCR Service unavailable:', error.message);
    return false;
  }
};

/**
 * Process a file with the local OCR service
 * @param {File} file - File object to process
 * @param {Function} onProgress - Callback for progress updates
 * @returns {Promise<Object>} Processed OCR results
 */
export const processOCRFile = async (file, onProgress = () => {}) => {
  try {
    // Check service health first
    onProgress({ status: 'Checking OCR service...', percentage: 5 });
    const isHealthy = await checkOCRServiceHealth();
    
    if (!isHealthy) {
      throw new Error(
        `OCR Service is not available at ${OCR_SERVICE_URL}. ` +
        'Please ensure the local OCR service is running on port 8020.'
      );
    }

    // Validate file format
    const supportedFormats = ['.pdf', '.jpeg', '.jpg', '.png', '.tiff', '.tif'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!supportedFormats.includes(fileExtension)) {
      throw new Error(
        `Unsupported file format: ${fileExtension}. ` +
        `Supported formats: ${supportedFormats.join(', ')}`
      );
    }

    // Validate file size (50 MB limit as per API docs)
    const maxFileSize = 50 * 1024 * 1024; // 50 MB
    if (file.size > maxFileSize) {
      throw new Error(
        `File size exceeds 50MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`
      );
    }

    // Create FormData for file upload
    onProgress({ status: 'Preparing file...', percentage: 10 });
    const formData = new FormData();
    formData.append('file', file);

    // Send file to OCR service
    onProgress({ status: 'Uploading and processing file...', percentage: 20 });
    console.log('Sending OCR request to:', OCR_API_ENDPOINT);
    console.log('File name:', file.name);
    console.log('File size:', (file.size / 1024 / 1024).toFixed(2), 'MB');

    // Record start time for processing duration
    const processingStartTime = Date.now();

    const response = await fetch(OCR_API_ENDPOINT, {
      method: 'POST',
      body: formData
      // Don't set Content-Type header - browser will set it with boundary
    });

    // Check if request was successful
    if (!response.ok) {
      const errorData = await response.json();
      const errorMessage = errorData.detail || errorData.message || 'Unknown error';
      throw new Error(
        `OCR Service error (${response.status}): ${errorMessage}`
      );
    }

    onProgress({ status: 'Processing results...', percentage: 80 });
    const ocrResponse = await response.json();

    // Calculate processing time in seconds
    const processingEndTime = Date.now();
    const processingTimeSeconds = ((processingEndTime - processingStartTime) / 1000).toFixed(2);

    console.log('OCR Response received:', ocrResponse);
    console.log('Pages processed:', ocrResponse.pages_processed);
    console.log('Processing time:', processingTimeSeconds, 'seconds');
    console.log('Number of results:', ocrResponse.results.length);

    // Process and format the results
    onProgress({ status: 'Formatting results...', percentage: 90 });
    const formattedResults = formatOCRResults(ocrResponse, file, processingTimeSeconds);

    onProgress({ status: 'Complete', percentage: 100 });

    return formattedResults;

  } catch (error) {
    console.error('OCR Processing Error:', error);
    throw error;
  }
};

/**
 * Format OCR service response into standard format
 * @param {Object} ocrResponse - Response from OCR service
 * @param {File} file - Original file object
 * @param {string} processingTimeSeconds - Processing time in seconds
 * @returns {Object} Formatted results
 */
const formatOCRResults = (ocrResponse, file, processingTimeSeconds = '0') => {
  try {
    // Extract text from all pages
    let allText = '';
    let successCount = 0;
    let failureCount = 0;
    let totalLines = 0;
    let extractionMethods = new Set();

    // Process each page result
    ocrResponse.results.forEach((pageResult, pageIdx) => {
      if (pageResult.status === 'success' && pageResult.text_lines) {
        successCount++;
        totalLines += pageResult.text_lines.length;
        
        // Combine all text with page separator
        if (allText) {
          allText += '\n\n--- Page ' + (pageIdx + 1) + ' ---\n';
        }
        allText += pageResult.text_lines.join('\n');
        
        // Track extraction method
        if (pageResult.metadata?.extraction_method) {
          extractionMethods.add(pageResult.metadata.extraction_method);
        }
        
        console.log(`Page ${pageIdx + 1}: ${pageResult.text_lines.length} lines extracted`);
      } else if (pageResult.status === 'no_text_found') {
        console.log(`Page ${pageIdx + 1}: No text found`);
      } else {
        failureCount++;
        console.error(`Page ${pageIdx + 1}: Processing failed`, pageResult.error);
      }
    });

    // Determine if processing was partially successful
    const partialSuccess = failureCount > 0 && successCount > 0;

    // Calculate confidence (average across all pages)
    let totalConfidence = 0;
    let confidenceCount = 0;
    ocrResponse.results.forEach((pageResult) => {
      if (pageResult.metadata?.average_confidence !== undefined) {
        totalConfidence += pageResult.metadata.average_confidence;
        confidenceCount++;
      }
    });
    const averageConfidence = confidenceCount > 0 ? totalConfidence / confidenceCount : 1.0;

    // Generate unique job ID from timestamp and file name
    const jobId = `ocr_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9]/g, '_')}`;

    // Build the response object
    const result = {
      text: allText || `No text could be extracted from the document.`,
      jobId,
      fileName: file.name,
      fileSize: file.size,
      uploadedAt: new Date().toISOString(),
      extractedTables: [], // Local OCR service doesn't extract tables separately
      partialSuccess,
      processingDetails: {
        totalPages: ocrResponse.pages_processed,
        pagesProcessed: ocrResponse.pages_processed,
        pagesSucceeded: successCount,
        pagesFailed: failureCount,
        totalLines: totalLines,
        averageConfidence: (averageConfidence * 100).toFixed(2) + '%',
        processingTime: `${processingTimeSeconds}s`
      },
      metadata: {
        source: 'Local OCR Service',
        serviceUrl: process.env.REACT_APP_OCR_SERVICE_URL || 'http://localhost:8020',
        fileName: ocrResponse.file_name,
        pagesProcessed: ocrResponse.pages_processed,
        successCount,
        failureCount,
        totalLines,
        averageConfidence: (averageConfidence * 100).toFixed(2) + '%',
        processingTime: processingTimeSeconds,
        extractionMethods: Array.from(extractionMethods),
        allResults: ocrResponse.results,
        processingTimestamp: new Date().toISOString()
      }
    };

    console.log('Formatted result:', {
      jobId: result.jobId,
      pagesProcessed: ocrResponse.pages_processed,
      successCount,
      failureCount,
      totalLinesExtracted: totalLines,
      averageConfidence: result.processingDetails.averageConfidence,
      textLength: result.text.length
    });

    return result;

  } catch (error) {
    console.error('Error formatting OCR results:', error);
    throw new Error(`Failed to format OCR results: ${error.message}`);
  }
};

/**
 * Legacy function names for backward compatibility
 */
export const createOCRJob = async (fileName) => {
  throw new Error('This function is deprecated. Use processOCRFile instead.');
};

export const getUploadUrls = async (jobId, fileName) => {
  throw new Error('This function is deprecated. Use processOCRFile instead.');
};

export const uploadFileToURL = async (uploadUrl, file) => {
  throw new Error('This function is deprecated. Use processOCRFile instead.');
};

export const startJobProcessing = async (jobId) => {
  throw new Error('This function is deprecated. Use processOCRFile instead.');
};

export const getJobStatus = async (jobId) => {
  throw new Error('This function is deprecated. Use processOCRFile instead.');
};
